import { Component, OnDestroy, OnInit, inject, output, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { GdpNotifyService } from '@features/gdp/services/gdp-notify.service';
import { NotificationsService } from '../notifications.service';
import { NOTIFICATION_TYPES, NotificationDTO, metaFor } from '../notification.model';

const PAGE_SIZE = 20;
/** Distancia al final (px) a partir de la cual se carga la siguiente página. */
const SCROLL_LOAD_THRESHOLD_PX = 200;

/**
 * Contenido del panel anclado bajo la campanita: filtros por tipo, lista con
 * lazy loading por scroll, y acciones "marcar todo visto" / "limpiar todo"
 * (esta última con confirmación inline, sin modal).
 */
@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslatePipe
  ],
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.css']
})
export class NotificationPanelComponent implements OnInit, OnDestroy {
  protected readonly svc = inject(NotificationsService);
  private readonly router = inject(Router);
  private readonly notify = inject(GdpNotifyService);

  /** El panel pide cerrarse (navegación, etc.). Lo escucha la campanita. */
  readonly closed = output<void>();

  protected readonly types = NOTIFICATION_TYPES;
  protected readonly metaFor = metaFor;

  protected readonly items = signal<NotificationDTO[]>([]);
  protected readonly total = signal(0);
  protected readonly loading = signal(false);
  protected readonly loadError = signal(false);
  protected readonly selectedTypes = signal<ReadonlySet<string>>(new Set());
  protected readonly confirmingClear = signal(false);
  /** Reloj para que los "hace X min" no se queden congelados con el panel abierto. */
  protected readonly now = signal(Date.now());

  protected readonly hasMore = computed(() => this.items().length < this.total());

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.load(true);
    interval(30_000).pipe(takeUntil(this.destroy$)).subscribe(() => this.now.set(Date.now()));

    // Si llega algo nuevo con el panel abierto, se muestra arriba al instante
    this.svc.events$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      const filter = this.selectedTypes();
      if (filter.size > 0 && !filter.has(event.type)) {
        return;
      }
      if (this.items().some(i => i.id === event.id)) {
        return;
      }
      this.items.update(list => [event, ...list]);
      this.total.update(t => t + 1);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ------------------------------------------------------------------ carga

  protected load(reset: boolean): void {
    if (this.loading()) {
      return;
    }
    this.loading.set(true);
    this.loadError.set(false);
    const offset = reset ? 0 : this.items().length;
    this.svc.list(offset, PAGE_SIZE, [...this.selectedTypes()]).subscribe({
      next: res => {
        if (reset) {
          this.items.set(res.items);
        } else {
          const known = new Set(this.items().map(i => i.id));
          this.items.update(list => [...list, ...res.items.filter(i => !known.has(i.id))]);
        }
        this.total.set(res.total);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.loadError.set(true);
      }
    });
  }

  /**
   * Fondo translúcido del icono de un ítem. Antes se concatenaba el alpha como
   * hex de 8 dígitos (`#rrggbb1f`), notación que Opera no interpreta y hacía que
   * TODOS los iconos de categoría se vieran rotos al mostrarlos juntos ("Todas").
   * `rgba()` es universal.
   */
  protected iconBg(type: string): string {
    const hex = metaFor(type).color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.12)`;
  }

  protected onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_THRESHOLD_PX;
    if (nearBottom && this.hasMore() && !this.loading()) {
      this.load(false);
    }
  }

  // ------------------------------------------------------------------ filtros

  protected isAllSelected(): boolean {
    return this.selectedTypes().size === 0;
  }

  protected selectAll(): void {
    if (this.isAllSelected()) {
      return;
    }
    this.selectedTypes.set(new Set());
    this.load(true);
  }

  protected toggleType(type: string): void {
    const next = new Set(this.selectedTypes());
    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
    }
    // Si quedan todos marcados, equivale a "Todas"
    if (next.size === this.types.length) {
      next.clear();
    }
    this.selectedTypes.set(next);
    this.load(true);
  }

  // ------------------------------------------------------------------ acciones

  protected openItem(n: NotificationDTO): void {
    if (!n.seen) {
      this.markSeenLocally(n.id);
      this.svc.markSeen(n.id).subscribe({ error: () => undefined });
    }
    if (n.link) {
      void this.router.navigateByUrl(n.link);
      this.closed.emit();
    }
  }

  protected deleteItem(n: NotificationDTO, event: MouseEvent): void {
    event.stopPropagation();
    const before = this.items();
    const beforeTotal = this.total();
    this.items.update(list => list.filter(i => i.id !== n.id));
    this.total.update(t => Math.max(0, t - 1));
    this.svc.deleteOne(n.id, !n.seen).subscribe({
      error: err => {
        this.items.set(before);
        this.total.set(beforeTotal);
        this.notify.error(err);
      }
    });
  }

  protected markAllSeen(): void {
    this.svc.markAllSeen().subscribe({
      next: () => this.items.update(list => list.map(i => (i.seen ? i : { ...i, seen: true }))),
      error: err => this.notify.error(err)
    });
  }

  protected askClearAll(): void {
    this.confirmingClear.set(true);
  }

  protected cancelClearAll(): void {
    this.confirmingClear.set(false);
  }

  protected confirmClearAll(): void {
    this.confirmingClear.set(false);
    this.svc.clearAll().subscribe({
      next: () => {
        this.items.set([]);
        this.total.set(0);
      },
      error: err => this.notify.error(err)
    });
  }

  protected goToPreferences(): void {
    this.closed.emit();
    void this.router.navigate(['/notifications/preferences']);
  }

  private markSeenLocally(id: number): void {
    this.items.update(list => list.map(i => (i.id === id ? { ...i, seen: true } : i)));
  }
}
